/**
 * BiomeRegistry - Registry for biome configuration definitions
 *
 * Manages biome visual configurations that BiomeManager uses to
 * instantiate biomes. Each biome can have its config defined in JSON
 * and loaded at startup.
 *
 * @example
 * import { biomeRegistry } from './registries/BiomeRegistry.js';
 *
 * // Load biomes from JSON
 * biomeRegistry.loadFromJSON(biomesData);
 *
 * // Get a specific biome config
 * const desertConfig = biomeRegistry.getConfig('desert');
 *
 * // Get biome for a given mileage
 * const biome = biomeRegistry.getBiomeForMiles(250);
 */
import { Registry } from '../core/Registry.js';
import { DEFAULT_CONFIG } from '../world/biomes/BiomeConfig.js';

// Valid color pattern - hex number format (0xRRGGBB or decimal)
const isValidColor = (value) => {
    if (typeof value === 'number') {
        return value >= 0 && value <= 0xFFFFFF;
    }
    if (typeof value === 'string') {
        // Support "0xRRGGBB" string format
        return /^0x[0-9a-fA-F]{6}$/.test(value);
    }
    return false;
};

// Parse color to number
const parseColor = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && /^0x[0-9a-fA-F]{6}$/.test(value)) {
        return parseInt(value, 16);
    }
    return null;
};

/**
 * Extended Registry for biome configuration definitions
 */
export class BiomeRegistry extends Registry {
    constructor() {
        super('Biomes');
        this._setupValidators();
    }

    /**
     * Set up biome-specific validators
     * @private
     */
    _setupValidators() {
        // Required fields validator
        this.addValidator((id, def) => {
            if (!def.name) return 'Biome must have a name';
            return null;
        });

        // Mile range validator (if provided)
        this.addValidator((id, def) => {
            if (def.minMiles !== undefined && def.maxMiles !== undefined) {
                if (typeof def.minMiles !== 'number' || typeof def.maxMiles !== 'number') {
                    return 'minMiles and maxMiles must be numbers';
                }
                if (def.minMiles >= def.maxMiles) {
                    return 'minMiles must be less than maxMiles';
                }
                if (def.minMiles < 0) {
                    return 'minMiles cannot be negative';
                }
            }
            return null;
        });

        // Terrain config validator
        this.addValidator((id, def) => {
            if (def.terrain) {
                if (def.terrain.color !== undefined && !isValidColor(def.terrain.color)) {
                    return `Invalid terrain.color: ${def.terrain.color}`;
                }
                if (def.terrain.shoulderColor !== undefined && !isValidColor(def.terrain.shoulderColor)) {
                    return `Invalid terrain.shoulderColor: ${def.terrain.shoulderColor}`;
                }
            }
            return null;
        });

        // Road config validator
        this.addValidator((id, def) => {
            if (def.road) {
                if (def.road.color !== undefined && !isValidColor(def.road.color)) {
                    return `Invalid road.color: ${def.road.color}`;
                }
                if (def.road.width !== undefined) {
                    if (typeof def.road.width !== 'number' || def.road.width <= 0) {
                        return 'road.width must be a positive number';
                    }
                }
            }
            return null;
        });

        // Objects config validator (probabilities must be 0-1)
        this.addValidator((id, def) => {
            if (def.objects) {
                if (def.objects.rocks) {
                    const rocks = def.objects.rocks;
                    if (rocks.probability !== undefined) {
                        if (typeof rocks.probability !== 'number' || rocks.probability < 0 || rocks.probability > 1) {
                            return 'objects.rocks.probability must be a number between 0 and 1';
                        }
                    }
                    if (rocks.minSize !== undefined && rocks.maxSize !== undefined) {
                        if (rocks.minSize > rocks.maxSize) {
                            return 'objects.rocks.minSize cannot be greater than maxSize';
                        }
                    }
                }
                if (def.objects.shrubs) {
                    const shrubs = def.objects.shrubs;
                    if (shrubs.probability !== undefined) {
                        if (typeof shrubs.probability !== 'number' || shrubs.probability < 0 || shrubs.probability > 1) {
                            return 'objects.shrubs.probability must be a number between 0 and 1';
                        }
                    }
                }
            }
            return null;
        });

        // Atmosphere config validator
        this.addValidator((id, def) => {
            if (def.atmosphere) {
                if (def.atmosphere.fogColor !== undefined && !isValidColor(def.atmosphere.fogColor)) {
                    return `Invalid atmosphere.fogColor: ${def.atmosphere.fogColor}`;
                }
                if (def.atmosphere.fogNear !== undefined && def.atmosphere.fogFar !== undefined) {
                    if (def.atmosphere.fogNear >= def.atmosphere.fogFar) {
                        return 'atmosphere.fogNear must be less than fogFar';
                    }
                }
            }
            return null;
        });
    }

    /**
     * Load biomes from JSON data
     * @param {Object} data - JSON data with biomes array
     * @returns {this} The registry instance for chaining
     */
    loadFromJSON(data) {
        if (!data.biomes || !Array.isArray(data.biomes)) {
            throw new Error('[Biomes] Invalid JSON: missing biomes array');
        }

        // Register each biome
        for (const biome of data.biomes) {
            if (!biome.id) {
                console.warn('[Biomes] Skipping biome without id:', biome);
                continue;
            }
            this.register(biome.id, biome);
        }

        console.log(`[Biomes] Loaded ${this.size} biomes from JSON`);
        return this;
    }

    /**
     * Get merged config for a biome (merges with DEFAULT_CONFIG)
     * @param {string} biomeId - The biome ID
     * @returns {Object} Merged configuration object
     */
    getConfig(biomeId) {
        const biome = this.get(biomeId);
        return this._mergeWithDefaults(biome);
    }

    /**
     * Get biome entry for a specific mileage.
     * If miles exceed all defined ranges, loops back through biomes cyclically.
     * @param {number} miles - The mileage to check
     * @returns {Object|null} The biome definition or null if no biomes defined
     */
    getBiomeForMiles(miles) {
        const sorted = this.getAllSorted();
        if (sorted.length === 0) return null;

        // First, check if miles falls within any defined range
        for (const biome of sorted) {
            if (biome.minMiles !== undefined && biome.maxMiles !== undefined) {
                if (miles >= biome.minMiles && miles < biome.maxMiles) {
                    return biome;
                }
            }
        }

        // Miles is outside all ranges - implement looping
        // Calculate the total cycle length (from first biome start to last biome end)
        const firstBiome = sorted[0];
        const lastBiome = sorted[sorted.length - 1];
        const cycleStart = firstBiome.minMiles;
        const cycleEnd = lastBiome.maxMiles;
        const cycleLength = cycleEnd - cycleStart;

        if (cycleLength <= 0) {
            // Fallback to first biome if ranges are invalid
            return firstBiome;
        }

        // Wrap miles into the cycle range
        const wrappedMiles = cycleStart + ((miles - cycleStart) % cycleLength);

        // Find biome for wrapped miles
        for (const biome of sorted) {
            if (biome.minMiles !== undefined && biome.maxMiles !== undefined) {
                if (wrappedMiles >= biome.minMiles && wrappedMiles < biome.maxMiles) {
                    return biome;
                }
            }
        }

        // If still no match (gaps in ranges), find the previous biome
        // by finding the last biome whose maxMiles <= wrappedMiles
        for (let i = sorted.length - 1; i >= 0; i--) {
            if (sorted[i].maxMiles <= wrappedMiles) {
                return sorted[i];
            }
        }

        // Ultimate fallback to first biome
        return firstBiome;
    }

    /**
     * Get all biomes sorted by minMiles
     * @returns {Array<Object>} Sorted array of biome definitions
     */
    getAllSorted() {
        return this.getAll()
            .filter(b => b.minMiles !== undefined)
            .sort((a, b) => a.minMiles - b.minMiles);
    }

    /**
     * Get information about the next biome transition from a given mileage.
     * Handles cycling through biomes when miles exceed all defined ranges.
     * @param {number} miles - Current mileage
     * @returns {Object|null} { name, atMile } or null if no next biome
     */
    getNextBiomeTransition(miles) {
        const sorted = this.getAllSorted();
        if (sorted.length === 0) return null;

        // Calculate cycle parameters for looping
        const firstBiome = sorted[0];
        const lastBiome = sorted[sorted.length - 1];
        const cycleStart = firstBiome.minMiles;
        const cycleEnd = lastBiome.maxMiles;
        const cycleLength = cycleEnd - cycleStart;

        if (cycleLength <= 0) return null;

        // Find current biome and the next transition point
        const currentBiome = this.getBiomeForMiles(miles);
        if (!currentBiome) return null;

        // Wrap current miles into cycle range for calculation
        const cycleNumber = Math.floor((miles - cycleStart) / cycleLength);
        const wrappedMiles = cycleStart + ((miles - cycleStart) % cycleLength);

        // Find the next biome in sequence
        const currentIndex = sorted.findIndex(b => b.id === currentBiome.id);
        const nextIndex = (currentIndex + 1) % sorted.length;
        const nextBiome = sorted[nextIndex];

        // Calculate the actual mile marker where transition occurs
        let transitionMile;
        if (nextIndex === 0) {
            // Wrapping to first biome - transition at end of cycle
            transitionMile = cycleEnd + (cycleNumber * cycleLength);
        } else {
            // Normal transition within cycle
            transitionMile = nextBiome.minMiles + (cycleNumber * cycleLength);
        }

        // If we're past this cycle's transition, add another cycle
        if (transitionMile <= miles) {
            transitionMile += cycleLength;
        }

        // Use a fancy sign name if available, otherwise fall back to generic name
        const displayName = nextBiome.signName || nextBiome.name;

        return {
            name: displayName,
            atMile: transitionMile
        };
    }

    /**
     * Merge biome JSON config with DEFAULT_CONFIG
     * Handles nested objects like terrain, road, objects
     * @private
     * @param {Object} biomeData - The biome definition from registry
     * @returns {Object} Merged configuration
     */
    _mergeWithDefaults(biomeData) {
        const config = { ...DEFAULT_CONFIG };

        // Merge terrain settings
        if (biomeData.terrain) {
            if (biomeData.terrain.color !== undefined) {
                config.terrainColor = parseColor(biomeData.terrain.color) ?? config.terrainColor;
            }
            if (biomeData.terrain.amplitudeLarge !== undefined) {
                config.terrainAmplitudeLarge = biomeData.terrain.amplitudeLarge;
            }
            if (biomeData.terrain.amplitudeMedium !== undefined) {
                config.terrainAmplitudeMedium = biomeData.terrain.amplitudeMedium;
            }
            if (biomeData.terrain.amplitudeSmall !== undefined) {
                config.terrainAmplitudeSmall = biomeData.terrain.amplitudeSmall;
            }
            if (biomeData.terrain.amplitudeFine !== undefined) {
                config.terrainAmplitudeFine = biomeData.terrain.amplitudeFine;
            }
            if (biomeData.terrain.heightMultiplier !== undefined) {
                config.terrainHeightMultiplier = biomeData.terrain.heightMultiplier;
            }
            if (biomeData.terrain.width !== undefined) {
                config.terrainWidth = biomeData.terrain.width;
            }
            if (biomeData.terrain.waterLevel !== undefined) {
                config.waterLevel = biomeData.terrain.waterLevel;
            }
        }

        // Merge road settings
        if (biomeData.road) {
            if (biomeData.road.width !== undefined) {
                config.roadWidth = biomeData.road.width;
            }
            if (biomeData.road.maxLateralOffset !== undefined) {
                config.roadMaxLateralOffset = biomeData.road.maxLateralOffset;
            }
            if (biomeData.road.maxVerticalOffset !== undefined) {
                config.roadMaxVerticalOffset = biomeData.road.maxVerticalOffset;
            }
        }

        // Merge object settings
        if (biomeData.objects) {
            if (biomeData.objects.density !== undefined) {
                config.roadsideObjectDensity = biomeData.objects.density;
            }
            if (biomeData.objects.rocks) {
                if (biomeData.objects.rocks.probability !== undefined) {
                    config.rockProbability = biomeData.objects.rocks.probability;
                }
                if (biomeData.objects.rocks.minSize !== undefined) {
                    config.rockMinSize = biomeData.objects.rocks.minSize;
                }
                if (biomeData.objects.rocks.maxSize !== undefined) {
                    config.rockMaxSize = biomeData.objects.rocks.maxSize;
                }
            }
            if (biomeData.objects.shrubs) {
                if (biomeData.objects.shrubs.minSize !== undefined) {
                    config.shrubMinSize = biomeData.objects.shrubs.minSize;
                }
                if (biomeData.objects.shrubs.maxSize !== undefined) {
                    config.shrubMaxSize = biomeData.objects.shrubs.maxSize;
                }
            }
        }

        // Merge atmosphere settings
        if (biomeData.atmosphere) {
            // Fog settings would be used by the biome renderer
            // Store them in config for access
            if (biomeData.atmosphere.fogNear !== undefined) {
                config.fogNear = biomeData.atmosphere.fogNear;
            }
            if (biomeData.atmosphere.fogFar !== undefined) {
                config.fogFar = biomeData.atmosphere.fogFar;
            }
            if (biomeData.atmosphere.fogColor !== undefined) {
                config.fogColor = parseColor(biomeData.atmosphere.fogColor) ?? config.fogColor;
            }
        }

        // Merge terrain features
        if (biomeData.terrainFeatures) {
            if (biomeData.terrainFeatures.enabled !== undefined) {
                config.terrainFeaturesEnabled = biomeData.terrainFeatures.enabled;
            }
            if (biomeData.terrainFeatures.scale !== undefined) {
                config.terrainFeatureScale = biomeData.terrainFeatures.scale;
            }
            if (biomeData.terrainFeatures.weights !== undefined) {
                config.terrainFeatureWeights = { ...config.terrainFeatureWeights, ...biomeData.terrainFeatures.weights };
            }
            if (biomeData.terrainFeatures.blend !== undefined) {
                config.terrainFeatureBlend = biomeData.terrainFeatures.blend;
            }
        }

        // Merge terrain layer heights and distances
        if (biomeData.terrainLayers) {
            if (biomeData.terrainLayers.heights !== undefined) {
                config.terrainLayerHeights = biomeData.terrainLayers.heights;
            }
            if (biomeData.terrainLayers.distances !== undefined) {
                config.terrainLayerDistances = biomeData.terrainLayers.distances;
            }
        }

        // Merge flat zone settings
        if (biomeData.flatZone) {
            if (biomeData.flatZone.min !== undefined) {
                config.terrainFlatZoneMin = biomeData.flatZone.min;
            }
            if (biomeData.flatZone.max !== undefined) {
                config.terrainFlatZoneMax = biomeData.flatZone.max;
            }
            if (biomeData.flatZone.blendRange !== undefined) {
                config.terrainBlendRange = biomeData.flatZone.blendRange;
            }
        }

        // Merge roadside strip settings
        if (biomeData.roadsideStrip) {
            if (biomeData.roadsideStrip.width !== undefined) {
                config.roadsideStripWidth = biomeData.roadsideStrip.width;
            }
            if (biomeData.roadsideStrip.widthLeft !== undefined) {
                config.roadsideStripWidthLeft = biomeData.roadsideStrip.widthLeft;
            }
            if (biomeData.roadsideStrip.widthRight !== undefined) {
                config.roadsideStripWidthRight = biomeData.roadsideStrip.widthRight;
            }
            if (biomeData.roadsideStrip.segments !== undefined) {
                config.roadsideStripSegments = biomeData.roadsideStrip.segments;
            }
            if (biomeData.roadsideStrip.overlap !== undefined) {
                config.roadsideStripOverlap = biomeData.roadsideStrip.overlap;
            }
            if (biomeData.roadsideStrip.mountainOverlap !== undefined) {
                config.mountainOverlapIntoStrip = biomeData.roadsideStrip.mountainOverlap;
            }
        }

        // Merge additional shrub settings
        if (biomeData.objects?.shrubs) {
            const shrubs = biomeData.objects.shrubs;
            if (shrubs.spriteCount !== undefined) {
                config.shrubSpriteCount = shrubs.spriteCount;
            }
            if (shrubs.scaleVariation !== undefined) {
                config.shrubScaleVariation = shrubs.scaleVariation;
            }
            if (shrubs.stretchVariation !== undefined) {
                config.shrubStretchVariation = shrubs.stretchVariation;
            }
        }

        // Merge per-side terrain settings (asymmetric terrain support)
        if (biomeData.terrainLeft) {
            config.terrainLeft = { ...biomeData.terrainLeft };
        }
        if (biomeData.terrainRight) {
            config.terrainRight = { ...biomeData.terrainRight };
        }

        // Pass through roadsideObjects config (used by RoadsideSpawner)
        if (biomeData.roadsideObjects) {
            config.roadsideObjects = biomeData.roadsideObjects;
        }

        return config;
    }
}

// Singleton instance for app-wide use
export const biomeRegistry = new BiomeRegistry();
